/**
 * VORE — carrossel (vore-carousel.js)
 *
 * Substitui as duas bibliotecas de slider do site original:
 *
 *   Swiper 11  — carrossel de especialistas da home (`expert_section`),
 *                `slidesPerView: 1.2` no mobile, 4 no desktop, scrollbar
 *                arrastavel e setas.
 *   Slick      — grid de 4 produtos da home abaixo de 767px
 *                (`slidesToShow: 2`, dots, `unslick` acima do breakpoint).
 *
 * Aqui as duas viram o mesmo componente: uma faixa com `scroll-snap` nativo
 * mais botoes, bolinhas e barra de progresso opcionais. Sem JS (e sem CSS de
 * JS) a faixa continua rolavel com o dedo e navegavel por teclado — os
 * controles so aparecem depois que o componente marca `active`.
 * Registrado em VORE-DIVERGENCIAS.md.
 *
 * Markup emitido pelas secoes:
 *
 *   <vore-carousel
 *     data-per-view="1.2"            slides visiveis quando ativo
 *     data-per-view-desktop="4"      slides visiveis acima do breakpoint
 *     data-active-below="1024">      so vira carrossel abaixo desta largura
 *     <div class="vore-carousel__viewport" data-vore-viewport> … slides … </div>
 *     <button data-vore-prev> <button data-vore-next>
 *     <div data-vore-scrollbar><span data-vore-drag></span></div>
 *     <div class="vore-carousel__dots"><button data-vore-dot="0"> … </div>
 *   </vore-carousel>
 *
 * `data-active-below` ausente ⇒ carrossel em todas as larguras.
 */

class RstCarousel extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-vore-viewport]');
    if (!this.viewport) return;

    this.slides = Array.from(this.viewport.children);
    this.prevButton = this.querySelector('[data-vore-prev]');
    this.nextButton = this.querySelector('[data-vore-next]');
    this.drag = this.querySelector('[data-vore-drag]');
    this.scrollbar = this.querySelector('[data-vore-scrollbar]');
    this.dots = Array.from(this.querySelectorAll('[data-vore-dot]'));

    this.perView = Number(this.dataset.perView) || 1;
    this.perViewDesktop = Number(this.dataset.perViewDesktop) || this.perView;

    const activeBelow = Number(this.dataset.activeBelow);
    this.query = activeBelow ? window.matchMedia(`(max-width: ${activeBelow - 0.02}px)`) : null;

    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onPrev = this.onPrev.bind(this);
    this.onNext = this.onNext.bind(this);
    this.onDot = this.onDot.bind(this);
    this.onScrollbarPointerDown = this.onScrollbarPointerDown.bind(this);
    this.onScrollbarPointerMove = this.onScrollbarPointerMove.bind(this);
    this.onScrollbarPointerUp = this.onScrollbarPointerUp.bind(this);

    this.viewport.addEventListener('scroll', this.onScroll, { passive: true });
    this.prevButton?.addEventListener('click', this.onPrev);
    this.nextButton?.addEventListener('click', this.onNext);
    this.dots.forEach((dot) => dot.addEventListener('click', this.onDot));
    this.scrollbar?.addEventListener('pointerdown', this.onScrollbarPointerDown);

    if (this.query) {
      this.query.addEventListener('change', this.onResize);
    }
    window.addEventListener('resize', this.onResize, { passive: true });

    this.applyMode();
  }

  disconnectedCallback() {
    this.viewport?.removeEventListener('scroll', this.onScroll);
    this.prevButton?.removeEventListener('click', this.onPrev);
    this.nextButton?.removeEventListener('click', this.onNext);
    this.dots.forEach((dot) => dot.removeEventListener('click', this.onDot));
    this.scrollbar?.removeEventListener('pointerdown', this.onScrollbarPointerDown);
    this.query?.removeEventListener('change', this.onResize);
    window.removeEventListener('resize', this.onResize);
    this.stopDragging();
  }

  /** Liga/desliga o modo carrossel e recalcula os slides visiveis. */
  applyMode() {
    const isActive = this.query ? this.query.matches : true;

    this.toggleAttribute('active', isActive);
    this.style.setProperty('--vore-carousel-per-view', String(isActive ? this.perView : this.perViewDesktop));

    if (isActive) {
      this.viewport.setAttribute('tabindex', '0');
      this.viewport.setAttribute('role', 'group');
    } else {
      this.viewport.removeAttribute('tabindex');
      this.viewport.removeAttribute('role');
    }

    this.update();
  }

  onResize() {
    this.applyMode();
  }

  onScroll() {
    if (this.frame) return;
    this.frame = window.requestAnimationFrame(() => {
      this.frame = null;
      this.update();
    });
  }

  /** Quanto rola a cada clique nas setas: um slide. */
  get step() {
    const [first, second] = this.slides;
    if (first && second) return Math.abs(second.offsetLeft - first.offsetLeft) || first.offsetWidth;
    return first ? first.offsetWidth : this.viewport.clientWidth;
  }

  get maxScroll() {
    return Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
  }

  onPrev() {
    this.viewport.scrollBy({ left: -this.step, behavior: 'smooth' });
  }

  onNext() {
    this.viewport.scrollBy({ left: this.step, behavior: 'smooth' });
  }

  /** @param {Event} event */
  onDot(event) {
    const button = event.currentTarget;
    if (!(button instanceof HTMLElement)) return;

    const index = Number(button.dataset.rstDot) || 0;
    const slide = this.slides[index];
    if (!slide) return;

    this.viewport.scrollTo({ left: slide.offsetLeft - this.slides[0].offsetLeft, behavior: 'smooth' });
  }

  /** Sincroniza setas, bolinhas e barra de progresso com a posicao atual. */
  update() {
    const { scrollLeft, clientWidth } = this.viewport;
    const max = this.maxScroll;
    const isActive = this.hasAttribute('active');

    if (this.prevButton) this.prevButton.disabled = !isActive || scrollLeft <= 1;
    if (this.nextButton) this.nextButton.disabled = !isActive || scrollLeft >= max - 1;

    if (this.drag && this.viewport.scrollWidth > 0) {
      const ratio = Math.min(1, clientWidth / this.viewport.scrollWidth);
      const progress = max > 0 ? scrollLeft / max : 0;
      this.drag.style.width = `${ratio * 100}%`;
      this.drag.style.transform = `translateX(${(progress * (1 - ratio) * 100) / (ratio || 1)}%)`;
    }

    if (this.dots.length > 0) {
      const step = this.step || 1;
      const current = Math.round(scrollLeft / step);
      this.dots.forEach((dot, index) => {
        dot.setAttribute('aria-current', index === current ? 'true' : 'false');
      });
    }
  }

  /** @param {PointerEvent} event */
  onScrollbarPointerDown(event) {
    if (!this.hasAttribute('active') && this.query) return;
    this.dragging = true;
    this.scrollbar?.setPointerCapture?.(event.pointerId);
    window.addEventListener('pointermove', this.onScrollbarPointerMove);
    window.addEventListener('pointerup', this.onScrollbarPointerUp);
    this.onScrollbarPointerMove(event);
  }

  /** @param {PointerEvent} event */
  onScrollbarPointerMove(event) {
    if (!this.dragging || !this.scrollbar) return;
    const rect = this.scrollbar.getBoundingClientRect();
    if (rect.width === 0) return;
    const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    this.viewport.scrollTo({ left: ratio * this.maxScroll, behavior: 'auto' });
  }

  onScrollbarPointerUp() {
    this.stopDragging();
  }

  stopDragging() {
    this.dragging = false;
    window.removeEventListener('pointermove', this.onScrollbarPointerMove);
    window.removeEventListener('pointerup', this.onScrollbarPointerUp);
  }
}

if (!customElements.get('vore-carousel')) {
  customElements.define('vore-carousel', RstCarousel);
}

export { RstCarousel };
